import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionManager } from 'experian-authentication-frontend-components';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { GuidedTourService } from 'ngx-guided-tour';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { stepsTour } from '../../../../src/app/services/steps';
import { LinksConfig } from '../../links.config';
import { TourEnum } from '../tour/tour.enum';
import { SedsModalService } from 'experian-design-system';
import { ModalVirtualTourComponent } from '../modal-virtual-tour/modal-vitual-tour.component';
import { ModalErrorComponent } from '../modal-error/modal-error.component';
import { Card } from '../../models/card.model';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {

  public accessToken: string;

  public ApplicationLinks;
  public linksConfig = new LinksConfig();

  public myCards: Card[];
  public currentYear = new Date().getFullYear();

  public isTouring: boolean;
  public guidedTour = stepsTour;
  public userId: string;
  public tourStatePrefix = 'virtual-tour';
  
  private readonly _jwtHelper = new JwtHelperService()
  private readonly destroy$: Subject<void> = new Subject();
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly sesisonManager = inject(SessionManager);
  private readonly _dialogRef = inject(MatDialog);
  private readonly modalService = inject(SedsModalService);
  
  constructor(
    public tourService: GuidedTourService
  ) { }

  public ngOnInit() {
    this.accessToken = this.sesisonManager.accessToken;
    const tokenExpired = this._jwtHelper.isTokenExpired(this.accessToken);
    const decodedToken = this.sesisonManager.decodedToken;
    this.userId = decodedToken ? decodedToken?.user_id : null;

    if (this.userId && !tokenExpired) {
      this._verifyTourStatus();
    }
    
    this._initSubscribers();
    this.ApplicationLinks = this.linksConfig;
  }

  public startTour(force = false): void {
    this.isTouring = true;
    try {
      this.tourService.startTour(this.guidedTour);
    } catch (error) {
      console.error('Erro ao iniciar tour', error);
    }
  }

  public clickModalInitTour(): void {
    setTimeout(() => {
      this.startTour(true);
    }, 500);
  }

  public logout($event: any): void {
    if ($event && $event.success) {
      this.router.navigate(['/login']);
    } else {
      this._openErrorModal();
    }
  }

  public response(response: any): void {
    if (response?.success) {
      if (response.data.products) {
        this.myCards = response.data.products;
      } else {
        this.myCards = null;
      }
    } else {
      this.myCards = null;

      if (!response.myProducts) {
        this._openErrorModal();
      }
    }
  }

  public renderResponse(response): void {
    if (!response.success) {
      this.myCards = null;
    } else {
      this.myCards = response && response.data && response.data.products;
    }
  }

  public skippedTour(): void {
    this._closeTourModal(TourEnum.SKIPPED);
  }

  public finishedTour(): void {
    this._closeTourModal(TourEnum.FINISHED);
  }

  private _closeTourModal(tourState: TourEnum): void {
    this.isTouring = false;
    localStorage.setItem(`${this.tourStatePrefix}-${this.userId}`, tourState);

    this.router.navigate(['./']);
    window.scrollTo({ top: 1 });
    window.scroll(0, 0);        
  }

  private _verifyTourStatus(): void {
    const tourStatus = localStorage.getItem(`${this.tourStatePrefix}-${this.userId}`);
    const digitalContractAccepted = this.sesisonManager.currentUser$.value?.flag?.isDigitalContractAccepted;

    if (!tourStatus && digitalContractAccepted) {
      this._openTourVirtualModal();
    }
  }

  private _openTourVirtualModal(): void {
    const data = {
      userId: this.userId
    };

    const options: MatDialogConfig = {
      width: '600px',
      disableClose: true,
      backdropClass: 'modal-backdrop'
    };

    this.modalService.open(ModalVirtualTourComponent, data, options);
  }

  private _openErrorModal(): void {
    this._dialogRef.openDialogs.forEach(dialog => dialog.close());

    const options: MatDialogConfig = {
      width: '600px',
      disableClose: true,
      backdropClass: 'modal-backdrop'
    };

    this.modalService.open(ModalErrorComponent, null, options);
  }  

  private _initSubscribers(): void {
      this.sesisonManager.logIn$.pipe(takeUntil(this.destroy$)).subscribe((response) => {
        this.router.navigate(['/terms']);
      }
    );

    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(
      (params) => {
        if (params.starttour !== undefined) {
          setTimeout(() => {
            this.startTour(params['starttour'] === 'force');
          }, 500);
        }
      }
    );

    this.guidedTour.skipCallback = () => {
      this.skippedTour();
    };

    this.guidedTour.completeCallback = () => {
      this.finishedTour();
    };
  }

  ngOnDestroy() {
    this._dialogRef.closeAll();
    this.destroy$.next();
    this.destroy$.complete();
  }

}